/**
 * Clears client caches on full browser reload (F5 / Cmd+R) so pages always refetch fresh data.
 * Home and screener reloads clear all caches except valid auth session.
 */

import { isAuthSessionValid } from './authTokens';
import { THEME_STORAGE_KEY } from './theme';
import {
  clearLiveMarketClientCache,
  LIVE_MARKET_EXTENDED_CACHE_KEY,
} from './liveMarketDataCache';
import { clearNewsClientCache } from './newsCache';
import { clearTradeDatesClientCache } from './tradeDatesCache';
import { clearSymbolNamesCache } from './symbolNamesCache';
import { clearMoversClientCache } from './moversCache';
import {
  clearAllPortfolioWatchlistClientCaches,
  clearPortfolioClientCaches,
  clearWatchlistClientCaches,
} from './portfolioWatchlistCache';

const AUTH_KEYS = ['user', 'cognito_tokens'];
/* User preferences + last extended-hours snapshot that must survive cache-clearing reloads */
const PRESERVED_KEYS = [THEME_STORAGE_KEY, LIVE_MARKET_EXTENDED_CACHE_KEY];
const SESSION_BYPASS_KEY = 'clientCacheFreshFetch';
const SESSION_RELOAD_SCOPE_KEY = 'clientCacheReloadScope';

const MARKET_DATA_KEYS = new Set([
  'tradeDatesCache',
  'latestTradeDateCache',
  'latestTradeDateByMarketCache',
  'symbolNamesCache',
  'newsPubDates',
  'newsDate',
  'selectedDate',
  'cachedStockData',
  'pwStockIndexCache',
  'liveMarketQuotesCache',
]);

const MARKET_DATA_PREFIXES = ['newsDataCache_'];

const SESSION_STORAGE_KEYS = ['screenerSessionMeta'];

export const isBrowserReload = () => {
  try {
    const [nav] = performance.getEntriesByType('navigation');
    if (nav && nav.type === 'reload') return true;
  } catch { /* ignore */ }
  if (typeof performance.navigation !== 'undefined' && performance.navigation.type === 1) {
    return true;
  }
  return false;
};

export const getPageCacheScope = (pathname = '') => {
  const path = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
  if (path === '/' || path === '/screener') return 'global';
  if (path.startsWith('/stock/')) return 'stock';
  if (path.startsWith('/portfolio')) return 'portfolio';
  if (path.startsWith('/watchlist')) return 'watchlist';
  if (path.startsWith('/news')) return 'news';
  if (path.startsWith('/movers')) return 'movers';
  if (path.startsWith('/messages')) return 'messages';
  return 'minimal';
};

const pruneExpiredAuth = () => {
  if (!isAuthSessionValid()) {
    AUTH_KEYS.forEach((key) => {
      try { localStorage.removeItem(key); } catch { /* ignore */ }
    });
  }
};

const shouldRemoveKey = (key, scope) => {
  if (!key || AUTH_KEYS.includes(key) || PRESERVED_KEYS.includes(key)) return false;

  if (scope === 'global') {
    return true;
  }

  if (MARKET_DATA_KEYS.has(key) || MARKET_DATA_PREFIXES.some((p) => key.startsWith(p))) {
    return ['global', 'stock', 'portfolio', 'watchlist', 'news', 'movers', 'minimal'].includes(scope);
  }

  if (key.startsWith('portfolioSymbolsCache_') || key.startsWith('portfolioData_')) {
    return scope === 'portfolio' || scope === 'global';
  }

  if (key.startsWith('watchlistSymbolsCache_') || key.startsWith('watchlistData_')) {
    return scope === 'watchlist' || scope === 'global';
  }

  return false;
};

const clearLocalStorageForScope = (scope) => {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (shouldRemoveKey(key, scope)) {
        localStorage.removeItem(key);
      }
    }
  } catch (error) {
    console.warn('[CLIENT CACHE RELOAD] localStorage clear failed:', error);
  }
};

const clearSessionStorageForScope = (scope) => {
  if (scope === 'global' || scope === 'stock') {
    SESSION_STORAGE_KEYS.forEach((key) => {
      try { sessionStorage.removeItem(key); } catch { /* ignore */ }
    });
  }
};

const resetInMemoryCaches = (scope) => {
  clearLiveMarketClientCache();

  if (['global', 'stock', 'portfolio', 'watchlist', 'news', 'movers', 'minimal'].includes(scope)) {
    clearNewsClientCache();
    clearTradeDatesClientCache();
    clearSymbolNamesCache();
  }

  if (scope === 'movers' || scope === 'global') {
    clearMoversClientCache();
  }

  if (scope === 'global') {
    clearAllPortfolioWatchlistClientCaches();
  } else if (scope === 'portfolio') {
    clearPortfolioClientCaches();
  } else if (scope === 'watchlist') {
    clearWatchlistClientCaches();
  }
};

/**
 * Run once at app bootstrap (before React render) on browser reload.
 */
export const handleClientCacheOnPageLoad = () => {
  if (typeof window === 'undefined' || !isBrowserReload()) return null;

  const scope = getPageCacheScope(window.location.pathname);
  console.log(`[CLIENT CACHE RELOAD] Browser reload on scope "${scope}" — clearing stale client caches`);

  clearLocalStorageForScope(scope);
  clearSessionStorageForScope(scope);
  resetInMemoryCaches(scope);
  pruneExpiredAuth();

  try {
    sessionStorage.setItem(SESSION_BYPASS_KEY, '1');
    sessionStorage.setItem(SESSION_RELOAD_SCOPE_KEY, scope);
  } catch { /* ignore */ }

  return scope;
};

/** True during the reload session until App finishes initial fresh fetches. */
export const isClientCacheBypassActive = () => {
  try {
    return sessionStorage.getItem(SESSION_BYPASS_KEY) === '1';
  } catch {
    return false;
  }
};

export const getClientCacheReloadScope = () => {
  try {
    return sessionStorage.getItem(SESSION_RELOAD_SCOPE_KEY);
  } catch {
    return null;
  }
};

export const clearClientCacheBypassFlag = () => {
  try {
    sessionStorage.removeItem(SESSION_BYPASS_KEY);
    sessionStorage.removeItem(SESSION_RELOAD_SCOPE_KEY);
  } catch { /* ignore */ }
};

export const shouldUseFreshClientCache = () => isClientCacheBypassActive();
